import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CustomCustomerFieldValue, Customer, CustomerProfileDetails, Gender } from '../../../../../../models/customer';

import { AccountService } from '../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import {
  CustomCustomerField,
  MailchimpGroupCategory
} from '../../../../../../models/organization-config';
import { getCustomerFieldValue } from '../../../../../../utils-models';
import { LocationService } from '../../../../../../services/location/location.service';
import {
  CallbackResult,
  CallbackResultStatus,
  CountryCodes,
  StateCodes
} from '../../../../../../utils';
import { EditCustomerMailchimpGroupsModalComponent } from './modals/edit-customer-mailchimp-groups-modal/edit-customer-mailchimp-groups-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomCustomerFieldType, MailchimpGroupCategoryType, ProfileField } from '../../../../../../models/organization-config-enums';
import {
  TakeCustomerWebcamPhotoModalComponent
} from './modals/take-customer-webcam-photo-modal/take-customer-webcam-photo-modal.component';
import { WebcamImage } from 'ngx-webcam';


/**
 * The component allowing customers' profile details to be edited.
 */
@Component({
  selector: 'app-edit-customer-profile',
  templateUrl: './edit-customer-profile.component.html',
  styleUrls: ['./edit-customer-profile.component.css']
})
export class EditCustomerProfileComponent implements OnInit {

  public customer: Customer;
  public customerProfileDetails: CustomerProfileDetails;
  public profileImageFileData: File = null;
  public profileImageBase64Data: string = null;

  public customCustomerFields: CustomCustomerField[] = [];
  public orgMailchimpGroupCategories: MailchimpGroupCategory[];

  public customerErrorMessage = '';
  public customerInviteErrorMessage = '';
  public customerErrors: any = {};

  public loadingCustomer = true;
  public loadingCustomerProfileDetails = true;
  public loadingCustomCustomerFields = true;
  public loadingOrgMailchimpGroupCategories = true;
  public loadingCustomerUpdate = false;
  public loadingCustomerInvite = false;
  public loadingGenerateBarcode = false;
  public loadingDownloadPrintableCustomerBarcode = false;
  public customerInviteComplete = false;

  public customerNotFound = false;
  public serverError: number = null;

  public modalResult: CallbackResult = null;

  public getCustomerFieldValue = getCustomerFieldValue;
  public ProfileField = ProfileField;
  public Gender = Gender;
  public MailchimpGroupCategoryType = MailchimpGroupCategoryType;
  public CallbackResultStatus = CallbackResultStatus;
  public CustomCustomerFieldType = CustomCustomerFieldType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private customerService: CustomerService,
    private modalService: NgbModal,
    public orgService: OrganizationConfigService,
    public locationService: LocationService
  ) { }

  public readonly CountryCodes = CountryCodes;
  public readonly StateCodes = StateCodes;

  ngOnInit() {
    const routeParams = this.route.snapshot.params;

    const customerId = parseInt(routeParams.id, 10);
    if (isNaN(customerId)) {
      this.customerNotFound = true;
      return;
    }

    this.loadCustomer(customerId);
    this.loadCustomerProfileDetails(customerId);
    this.loadCustomCustomerFields();
    this.loadOrgMailchimpGroupCategories();
  }

  /**
   * Load the customer with the given ID.
   *
   * @param customerId - The ID of the customer.
   */
  public loadCustomer(customerId: number) {
    this.loadingCustomer = true;

    this.customerService
      .getCustomer(customerId)
      .then(customer => { this.customer = customer; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomer = false; });
  }

  public loadCustomerProfileDetails(customerId: number) {
    this.loadingCustomerProfileDetails = true;

    this.customerService
      .getCustomerProfileDetails(customerId)
      .then(customer => { this.customerProfileDetails = customer; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomerProfileDetails = false; });
  }

  public loadCustomCustomerFields() {
    this.loadingCustomCustomerFields = true;

    this.orgService
      .getCustomCustomerFields(1, 3000)
      .then(result => { this.customCustomerFields = result.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomCustomerFields = false; });
  }

  public loadOrgMailchimpGroupCategories() {
    this.loadingOrgMailchimpGroupCategories = true;

    this.orgService
      .getMailchimpGroupCategories()
      .then(result => { this.orgMailchimpGroupCategories = result; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingOrgMailchimpGroupCategories = false; });
  }

  /**
   * Save the updated customer profile info to the API.
   */
  public saveCustomer() {
    this.loadingCustomerUpdate = true;

    this.customerErrorMessage = '';
    this.customerErrors = {};

    // Null indicates no email, not empty string.
    if (this.customer.email === '') {
      this.customer.email = null;
    }

    this.customerService
      .updateCustomer(
        this.customer,
        this.profileImageFileData,
        this.profileImageBase64Data,
      )
      .then(updatedCustomer => {
        const successMessage = updatedCustomer.first_name + ' ' + updatedCustomer.last_name + ' saved successfully.';

        this.router.navigate(
          ['/customers/' + updatedCustomer.id.toString()],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.customerErrors = reason.error;
          this.customerErrorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.customerErrorMessage = 'You do not have permission to modify this customer.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomerUpdate = false; });
  }

  /**
   * The event handler for the profile image file input.
   * - Ensures that the file is an image.
   * - Saves the file data in profileImageFileData.
   * - Sets the file location as the customer's profile image (for preview display).
   */
  public attachProfileImageFile(event) {
    this.profileImageFileData = event.target.files[0] as File;
    if (this.profileImageFileData.type.match(/image\/*/) == null) {
      console.error('Non-file type image upload: ', this.profileImageFileData.type);
      this.profileImageFileData = null;
      return;
    }

    this.profileImageBase64Data = null;

    const reader = new FileReader();
    reader.readAsDataURL(this.profileImageFileData);
    reader.onload = () => {
      this.customer.profile_image = reader.result as string;
    };
  }

  public sendInviteEmail() {
    this.customerInviteErrorMessage = '';
    this.loadingCustomerInvite = true;

    if (!this.customer.email) {
      this.customerInviteErrorMessage = 'Customer has no email address.';
      this.loadingCustomerInvite = false;
      return;
    }

    this.customerService
      .sendCustomerInvite(this.customer)
      .then(() => {
        this.customer.capitan_invite_sent = true;
        this.customerInviteComplete = true;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.customerInviteErrorMessage = reason.error;
        } else if (reason.status === 403) {
          this.customerErrorMessage = 'You do not have permission to send an invite to this customer.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomerInvite = false; });
  }

  public generateBarcode() {
    this.loadingGenerateBarcode = true;

    this.customerService
      .generateRandomCustomerBarcode()
      .then(barcode => { this.customer.barcode = barcode; })
      .catch(reason => {
        if (reason.status === 403) {
          this.customerErrorMessage = 'You do not have permission to generate barcodes.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingGenerateBarcode = false; });
  }

  public downloadPrintableCustomerBarcode() {
    if (this.loadingDownloadPrintableCustomerBarcode) { return; }

    this.loadingDownloadPrintableCustomerBarcode = true;

    this.customerService
      .updateCustomerBarcode(this.customer)
      .then(customer => {
          return this.customerService
            .downloadPrintableCustomerBarcode(this.customer);
        }
      )
      .catch(reason => {
        if (reason.status === 400) {
          this.customerErrors = reason.error;
          this.customerErrorMessage = 'Please correct the errors above and try again.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingDownloadPrintableCustomerBarcode = false;
      });
  }

  public hasConnectedMailchimpAccount() {
    return (
      this.orgService.getOrganizationConfig() &&
      !!this.orgService.getOrganizationConfig().mailchimp_audience_id
    );
  }

  public getCustomerMailchimpGroupNamesInCategory(category: MailchimpGroupCategory): string[] {
    if (!this.customerProfileDetails || !this.orgMailchimpGroupCategories) {
      return [];
    }

    const groupNames = [];

    for (const group of category.groups) {
      if (this.customerProfileDetails.mailchimp_group_ids.includes(group.id)) {
        groupNames.push(group.name);
      }
    }

    return groupNames;
  }

  public openEditMailchimpGroupsModal() {
    if (!this.customerProfileDetails || !this.orgMailchimpGroupCategories) {
      return;
    }

    this.modalResult = null;

    const modalRef = this.modalService.open(
      EditCustomerMailchimpGroupsModalComponent
    );
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.customerProfileDetails = this.customerProfileDetails;
    modalRef.componentInstance.orgMailchimpGroupCategories = this.orgMailchimpGroupCategories;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadCustomerProfileDetails(this.customer.id);
    }
  }

  public openTakeCustomerWebcamPhotoModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(
      TakeCustomerWebcamPhotoModalComponent,
      {
        size: 'lg',
      }
    );
    modalRef.componentInstance.imageCapturedCallback = (image: WebcamImage) => {
      this.customer.profile_image = image.imageAsDataUrl;
      this.profileImageBase64Data = image.imageAsBase64;
      this.profileImageFileData = null;
    }
  }

  public getCustomFieldValueSingle(field: CustomCustomerField): CustomCustomerFieldValue {
    let matchedFV: CustomCustomerFieldValue = null;
    for (const fv of this.customer.custom_field_values) {
      if (fv.field_id === field.id) {
        matchedFV = fv;
      }
    }
    if (matchedFV === null) {
      matchedFV = {
        field_id: field.id,
        field_name: field.name,
        value: ''
      };
      this.customer.custom_field_values.push(matchedFV);
    }
    return matchedFV;
  }

  public getCustomFieldValueMultiple(field: CustomCustomerField): string[] {
    const matchedFVs: string[] = [];
    for (const fv of this.customer.custom_field_values) {
      if (fv.field_id === field.id) {
        matchedFVs.push(fv.value);
      }
    }
    return matchedFVs;
  }

  public toggleCustomCheckBoxesFieldValue(field: CustomCustomerField, value: string) {
    for (let i = 0; i < this.customer.custom_field_values.length; i++) {
      const fv = this.customer.custom_field_values[i];
      if ((fv.field_id === field.id) && (fv.value === value)) {
        this.customer.custom_field_values.splice(i, 1);
        return;
      }
    }

    // If the value was not already selected (and removed), add it.
    this.customer.custom_field_values.push({
      field_id: field.id,
      field_name: field.name,
      value
    });
  }
}


